import { emptySplitApi as api } from "./emptyApi"

/** One published revision of an artifact (newest first in `versions`). */
export type CompanyArtifactVersion = {
  version: number
  sizeBytes: number
  sha256: string
  sourceUrl: string | null
  note: string | null
  createdByEmail: string | null
  createdAt: string
}

/** PRIVATE: the company's signed-in users. PUBLIC: anyone with the share link. */
export type ArtifactVisibility = "PRIVATE" | "PUBLIC"

/** Metadata only; the page comes from `getCompanyArtifactContent`. */
export type CompanyArtifact = {
  id: string
  title: string
  description: string | null
  currentVersion: number
  visibility: ArtifactVisibility
  /** Token of the share link while PUBLIC; null while PRIVATE. */
  publicToken: string | null
  createdAt: string
  updatedAt: string
  versions?: CompanyArtifactVersion[]
}

/** What the login-free share page gets. */
export type PublicArtifact = {
  title: string
  description: string | null
  version: number
  updatedAt: string
  html: string
}

/** The login-free link for a PUBLIC artifact. */
export function artifactShareUrl(publicToken: string): string {
  return `${window.location.origin}/share/artifacts/${publicToken}`
}

export type CompanyArtifactContent = {
  id: string
  version: number
  /** CSP-wrapped page for a sandboxed iframe srcdoc. Never render it on the app origin. */
  html: string
}

export type PublishCompanyArtifactBody = {
  artifactId?: string
  title?: string
  description?: string
  html: string
  sourceUrl?: string
  note?: string
  visibility?: ArtifactVisibility
}

export type PublishCompanyArtifactResult = CompanyArtifact & {
  warnings: string[]
}

export type UpdateCompanyArtifactBody = {
  title?: string
  description?: string | null
  currentVersion?: number
  visibility?: ArtifactVisibility
}

const LIST_ID = "LIST" as const
const TAG = "CompanyArtifact" as const

export const companyArtifactApi = api.injectEndpoints({
  endpoints: build => ({
    getCompanyArtifacts: build.query<CompanyArtifact[], void>({
      query: () => ({ url: "/company-artifacts" }),
      providesTags: result => [
        { type: TAG, id: LIST_ID },
        ...(result ?? []).map(a => ({ type: TAG, id: a.id })),
      ],
    }),
    getCompanyArtifact: build.query<CompanyArtifact, string>({
      query: id => ({ url: `/company-artifacts/${id}` }),
      providesTags: (_result, _err, id) => [{ type: TAG, id }],
    }),
    getCompanyArtifactContent: build.query<
      CompanyArtifactContent,
      { id: string; version: number }
    >({
      // Versions are immutable, so (id, version) content never goes stale.
      query: ({ id, version }) => ({
        url: `/company-artifacts/${id}/content`,
        params: { version },
      }),
      keepUnusedDataFor: 300,
    }),
    /** Public (unauthenticated) share page. */
    getPublicArtifact: build.query<PublicArtifact, string>({
      query: token => ({ url: `/public/artifacts/${token}` }),
    }),
    publishCompanyArtifact: build.mutation<
      PublishCompanyArtifactResult,
      PublishCompanyArtifactBody
    >({
      query: body => ({ url: "/company-artifacts", method: "POST", body }),
      invalidatesTags: (_result, _err, body) => [
        { type: TAG, id: LIST_ID },
        ...(body.artifactId ? [{ type: TAG, id: body.artifactId }] : []),
      ],
    }),
    updateCompanyArtifact: build.mutation<
      CompanyArtifact,
      { id: string; body: UpdateCompanyArtifactBody }
    >({
      query: ({ id, body }) => ({
        url: `/company-artifacts/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        { type: TAG, id },
        { type: TAG, id: LIST_ID },
      ],
    }),
    deleteCompanyArtifact: build.mutation<CompanyArtifact, string>({
      query: id => ({ url: `/company-artifacts/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: TAG, id: LIST_ID }],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetCompanyArtifactsQuery,
  useGetCompanyArtifactQuery,
  useGetCompanyArtifactContentQuery,
  useGetPublicArtifactQuery,
  usePublishCompanyArtifactMutation,
  useUpdateCompanyArtifactMutation,
  useDeleteCompanyArtifactMutation,
} = companyArtifactApi
